import React, { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useLang } from '@/contexts/LanguageContext';
import { NAV } from '@/constants/testIds';
import JellyLogo from '@/components/JellyLogo';
import CartButton from '@/components/CartButton';

export default function Navbar() {
  const { t, lang, toggle } = useLang();
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  const links = [
    { to: '/', label: t('nav.home'), id: NAV.home },
    { to: '/productos', label: t('nav.products'), id: NAV.products },
    { to: '/sobre', label: t('nav.about'), id: NAV.about },
    { to: '/registro', label: t('nav.registration'), id: 'nav-registration' },
    { to: '/sorteo', label: t('nav.raffle'), id: 'nav-raffle', live: true },
    { to: '/distribuidores', label: t('nav.distributors'), id: NAV.distributors },
    { to: '/contacto', label: t('nav.contact'), id: NAV.contact },
  ];

  return (
    <header className="sticky top-0 z-40 glass">
      <div className="max-w-7xl mx-auto px-5 md:px-10 h-24 md:h-28 flex items-center justify-between">
        <Link to="/" data-testid={NAV.brand} className="flex items-center gap-2 group">
          <JellyLogo size="md" color="#FF2D9C" bylineColor="#FF2D9C" />
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.id}
              className={({ isActive }) =>
                `text-sm font-medium tracking-wide hover:text-[#FF7597] transition-colors ${
                  isActive ? 'text-[#FF7597]' : 'text-[#1F1A24]/80'
                }`
              }
              end={l.to === '/'}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid={NAV.languageToggle}
            onClick={toggle}
            aria-label="Toggle language"
            className="hidden sm:flex h-9 px-3 items-center rounded-full border border-[#FF7597]/30 text-xs font-bold tracking-widest text-[#1F1A24] hover:bg-[#FF2D9C] hover:text-white hover:border-[#FF2D9C] transition-all"
          >
            {lang === 'es' ? 'ES · EN' : 'EN · ES'}
          </button>
          <CartButton testId="nav-cart-btn" />
          <Link
            to="/distribuidores"
            className="hidden md:inline-flex h-10 px-5 items-center rounded-full bg-[#1F1A24] text-white text-xs font-bold tracking-widest hover:bg-[#FF7597] transition-colors"
          >
            B2B
          </Link>
          <button
            data-testid={NAV.mobileMenuBtn}
            className="lg:hidden h-10 w-10 inline-flex items-center justify-center rounded-full border border-[#FF7597]/30"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-[#FFFBFB] flex flex-col p-6 animate-fade-up lg:hidden">
          <div className="flex justify-between items-center mb-10">
            <JellyLogo size="md" color="#FF2D9C" bylineColor="#FF2D9C" />
            <button
              data-testid={NAV.mobileMenuClose}
              onClick={() => setOpen(false)}
              className="h-10 w-10 inline-flex items-center justify-center rounded-full border border-[#FF7597]/30"
              aria-label="Close menu"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <nav className="flex flex-col gap-5">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={close}
                data-testid={`${l.id}-mobile`}
                className={({ isActive }) =>
                  `inline-flex items-center gap-2 font-display text-3xl font-bold tracking-tighter ${
                    isActive ? 'text-[#FF7597]' : 'text-[#1F1A24]'
                  }`
                }
                end={l.to === '/'}
              >
                {l.live && (
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF5C83] opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#FF5C83]"></span>
                  </span>
                )}
                {l.label}
              </NavLink>
            ))}
          </nav>
          <button
            data-testid={`${NAV.languageToggle}-mobile`}
            onClick={toggle}
            className="mt-10 self-start px-5 py-3 rounded-full border border-[#FF7597]/30 text-xs font-bold tracking-widest"
          >
            {lang === 'es' ? 'Switch to English' : 'Cambiar a Español'}
          </button>
        </div>
      )}
    </header>
  );
}
